import React, { useRef } from 'react';
import { useInView } from 'react-intersection-observer';
import styled from 'styled-components';
import { useDraggable } from '@dnd-kit/core';
import { CircularProgress, Typography } from '@material-ui/core';
import { useKanbanBoardContext } from './KanbanBoardContext';
const StyledDiv = styled.div `
  position: relative;
  display: flex;
  flex-direction: column;
  border-radius: 4px;
  background: #fff;
  box-shadow: 0px 1px 4px rgba(0, 0, 0, 0.08);
  margin-bottom: 10px;
  min-height: min-content;
  border: 0px solid;
  &:last-child {
    margin-bottom: 0;
  }
  &.dragging {
    opacity: 0.8;
    background: #fff;
    border-radius: 4px;
    box-shadow: 0px 2px 1px -1px rgba(0, 0, 0, 0.2),
      0px 1px 1px 0px rgba(0, 0, 0, 0.14), 0px 1px 3px 0px rgba(0, 0, 0, 0.12);
  }
  &:not(.drag-disabled) {
    cursor: pointer;
  }
  &.deleted {
    border-left: solid 4px ${p => p.theme.palette.error.main};
    margin-left: -4px;
  }
  &:hover .hover-only {
    opacity: 1;
    max-height: 100px;
  }
  .hover-only {
    opacity: 0;
    max-height: 0;
    overflow: hidden;
    transition: opacity 300ms, max-height 300ms;
    transition-timing-function: cubic-bezier(0.4, 0.82, 0.165, 1);
  }
  .content {
    display: flex;
    flex-direction: column;
    padding: 12px;
    margin: 0;
  }
  .title-container {
    display: flex;
    align-items: center;
    padding-bottom: 6px;
    white-space: pre-wrap;
  }
  .title-icon {
    padding-right: 6px;
  }
  .title {
    flex: 1;
    font-weight: bold;
    padding-right: 12px;
  }
  .title-accessory {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    align-self: flex-start;
    overflow: hidden;
  }
  .description-box {
    .description {
      position: relative;
      z-index: 0;
      white-space: pre-wrap;
      line-height: 1.43;
      &:hover::before {
        content: '';
        display: block;
        position: absolute;
        top: -4px;
        left: -4px;
        right: -4px;
        bottom: -4px;
        background: #f7f8fc;
        border: 1px solid #f4f4f4;
        border-radius: 4px;
        z-index: -1;
      }
    }
  }
  .card-loading {
    position: absolute;
    display: flex;
    justify-content: center;
    align-items: center;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(255, 255, 255, 0.75);
    border-radius: 4px;
  }
`;
export const KanbanBoardCard = props => {
    var _a, _b, _c, _d;
    const { item, column, isDragging = false, style: styleProp, containerProps, innerRef, onSize } = props;
    const elementRef = useRef(null);
    const { ref, entry, inView } = useInView({
        threshold: 0,
        rootMargin: '100px'
    });
    const { disabled, isItemLoading, onSelectItem } = useKanbanBoardContext();
    const disableDrag = disabled || props.disableDrag;
    const isLoading = isItemLoading(item);
    const classes = ['board-card'];
    if (isDragging)
        classes.push('dragging');
    if (disableDrag)
        classes.push('drag-disabled');
    if (item.deleted)
        classes.push('deleted');
    // save minHeight so that hiding the content
    // does not affect container height and the scrollbar.
    // override saved styling from parent if entries exist
    const style = Object.assign({}, styleProp);
    if (entry) {
        const height = entry.boundingClientRect.height;
        onSize === null || onSize === void 0 ? void 0 : onSize(height);
        style.minHeight = height;
    }
    if (inView) {
        delete style.minHeight;
    }
    const renderOptions = {
        item,
        column,
        isDragging,
        elementRef,
        cardStyle: style
    };
    const card = (_a = column.renderCard) === null || _a === void 0 ? void 0 : _a.call(column, renderOptions);
    if (card) {
        return card;
    }
    const titleIcon = (_b = column.renderTitleIcon) === null || _b === void 0 ? void 0 : _b.call(column, renderOptions);
    const titleAccessory = (_c = column.renderTitleAccessory) === null || _c === void 0 ? void 0 : _c.call(column, renderOptions);
    return (React.createElement(StyledDiv, Object.assign({}, containerProps, { style: style, className: classes.join(' '), ref: element => {
            ref(element);
            innerRef === null || innerRef === void 0 ? void 0 : innerRef(element);
            elementRef.current = element;
        }, onClick: () => onSelectItem === null || onSelectItem === void 0 ? void 0 : onSelectItem(item) }), inView && (React.createElement(React.Fragment, null,
        React.createElement("div", { className: 'content' },
            React.createElement("div", { className: 'title-container' },
                titleIcon && React.createElement("div", { className: 'title-icon' }, titleIcon),
                React.createElement(Typography, { variant: 'body1', className: 'title' }, item.title),
                titleAccessory && (React.createElement("div", { className: 'title-accessory' }, titleAccessory))),
            React.createElement("div", { className: 'description-box' },
                React.createElement(Typography, { variant: 'body2', className: 'description', color: item.description ? 'initial' : 'textSecondary' }, item.description))), (_d = column.renderSubContent) === null || _d === void 0 ? void 0 :
        _d.call(column, renderOptions),
        isLoading && (React.createElement("div", { className: 'card-loading' },
            React.createElement(CircularProgress, { size: 24, color: 'primary' })))))));
};
export default KanbanBoardCard;
export const DraggableKanbanBoardCard = props => {
    const { item } = props;
    // make sure id and taskId are available to allow dragging
    const { disabled } = useKanbanBoardContext();
    const disableDrag = disabled || !item.id || !item.taskId;
    const { attributes, isDragging, listeners, setNodeRef } = useDraggable({
        id: item.id.toString(),
        disabled: disableDrag
    });
    return (React.createElement(KanbanBoardCard, Object.assign({}, props, { innerRef: setNodeRef, isDragging: isDragging, disableDrag: disableDrag, containerProps: Object.assign(Object.assign({}, attributes), listeners) })));
};
//# sourceMappingURL=KanbanBoardCard.js.map